import { Fragment, type FC } from 'react';
import { Space, Text } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';
import { format, parseISO } from 'date-fns';
import { Avatar, Empty, Spin } from 'antd';
import he from 'he';

import { type JsonData, type JsonDataItem, type WithOptionalDataTest } from 'types';
import styles from './task-notes-and-logs.module.scss';

type NoteLabels = {
  [key: string]: string;
};
type Props = {
  logs: JsonData;
  notes: JsonData;
  noteLabels?: NoteLabels;
  showLogs?: boolean;
  isLoading?: boolean;
} & WithOptionalDataTest;

export const TaskNotesAndLogs: FC<Props> = observer((props) => {
  const { logs, notes, showLogs, isLoading, noteLabels } = props;
  const listIems = showLogs ? logs : notes;

  const formatLog = (data: JsonData): JsonDataItem => {
    if (!data?.length) {
      return [];
    }

    return [...data].reduce((acc: JsonDataItem, item: JsonDataItem) => {
      const dateTime = item.created || item.added_at || item.entered_at;
      const text = item.value || item.note;

      const date = format(parseISO(dateTime), 'EEEE dd MMM yyyy');
      const time = format(parseISO(dateTime), 'h:mmaaa').toLowerCase();

      if (!acc[date]) {
        acc[date] = [];
      }

      acc[date].push({
        id: item.ID,
        name: item.user?.name || 'Unknown',
        time: time,
        description: text || '',
        type: item.type,
      });

      return acc;
    }, {});
  };

  if (isLoading) {
    return (
      <Space.$ justify={Space.Justify.Center} className={styles.taskNotesAndLogs__placeholder}>
        <Spin style={{ margin: '36px auto' }} />
      </Space.$>
    );
  }

  return (
    <div className={styles.taskNotesAndLogs__log}>
      <Space.$
        direction={Space.Direction.Vertical}
        size={Space.Size.Large}
        className={styles.taskNotesAndLogs__logContent}
      >
        {!listIems?.length && (
          <Empty
            rootClassName={styles.taskNotesAndLogs__placeholder}
            styles={{ image: { height: 60 } }}
            description={<Text.$ type="secondary">No data</Text.$>}
          />
        )}
        {Object.keys(formatLog(listIems)).map((day: string) => (
          <Fragment key={day}>
            <div className={styles.taskNotesAndLogs__logDate}>{day}</div>
            {formatLog(listIems)[day]?.map((item: JsonDataItem) => (
              <Space.$
                key={item.id}
                className={styles.taskNotesAndLogs__logItem}
                size={Space.Size.Middle}
                align={Space.Align.Start}
                justify={Space.Justify.SpaceBetween}
              >
                <Space.$ align={Space.Align.Start}>
                  <Avatar>
                    {item.name
                      ?.toUpperCase()
                      .split(' ')
                      .map((word: string) => word[0])
                      .slice(0, 2)
                      .join('')}
                  </Avatar>
                  <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
                    <Space.$>
                      <Text.$ size={Text.Size.LG} strong>
                        {he.decode(item.name)}
                      </Text.$>
                      <Text.$>{item.time}</Text.$>
                    </Space.$>
                    <Text.$ type="secondary"> {item.description}</Text.$>
                  </Space.$>
                </Space.$>
                {noteLabels && (
                  <Text.$ strong size={Text.Size.MD} className={styles.taskNotesAndLogs__logItemType}>
                    {noteLabels[item.type]}
                  </Text.$>
                )}
              </Space.$>
            ))}
          </Fragment>
        ))}
      </Space.$>
    </div>
  );
});
